// @spec GHG-STR-066
'use client';

import { createFileRoute } from '@tanstack/react-router';

import { useGHGRole } from '@/features/ghg/hooks/useGHGRole';
import { GHGSettingsCard } from '@/features/settings/ghg';
import { useOrganizationId } from '@/hooks/use-organization-id';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/ghg/settings/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.ghgSettings'),
  }),
  component: GHGSettingsPage,
});

/**
 * Organization GHG settings page for managing GHG module configuration.
 *
 * Allows organization admins to:
 * - Configure Scope 3 category enablement and calculation methods
 * - Set emission factor preferences (source, GWP version)
 * - Define data quality requirements
 * - Configure approval workflows
 * - Manage notification preferences
 */
function GHGSettingsPage() {
  const { t } = useTranslation('ghg');
  const { isAdmin: isClerkAdmin } = useGHGRole();
  const organizationId = useOrganizationId();
  const { organizations } = useOrganizations();
  const currentOrganization =
    organizations.find((org) => org.id === organizationId || org.authOrganizationId === organizationId) ??
    organizations.find((org) => org.isActive) ??
    undefined;
  const normalizedRole = currentOrganization?.role.name.trim().toLowerCase();
  const isBackendAdmin = normalizedRole?.includes('admin') ?? false;
  const isAdmin = isClerkAdmin || isBackendAdmin;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('settingsPage.heading')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('settingsPage.description')}
          </p>
        </header>

        <section className="space-y-6">
          <GHGSettingsCard isAdmin={isAdmin} />
        </section>
      </div>
    </div>
  );
}
